import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormValidation } from '@makemydeal/dr-shared-ui-utils';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import { getTogglesWithoutSafetyChecks } from '@makemydeal/dr-shared-store/selectors/featureToggleSelectors';
import type { ValidationEngine } from '@makemydeal/dr-shared-ui-utils/validationEngine/ValidationEngine';
import type { UseValidationEngineOptions } from '@makemydeal/dr-shared-ui-utils/validationEngine/hooks/useFormValidation';

export type ContractFlowValidationEngine = ValidationEngine & {
    /**
     * Checks if any of the currently errored fields fail a contract-scoped rule.
     * This avoids scanning every registered field when the goal is to understand whether
     * existing errors are specifically contract-related.
     * Note: This assumes component pre-runs the validate all step before invoking this method, so that all current errors are populated in the engine state.
     */
    getHasContractErrors: () => boolean;
};

export type UseContractFlowAdapterResult = {
    engine: ContractFlowValidationEngine;
    /** True when any currently invalid field fails a contract-scoped rule. */
    hasContractErrors: boolean;
};

/**
 * @deprecated Use the generic useFormValidation hook instead.
 * Adapter hook that augments a validation engine with contract-flow behavior and exposes
 * a React-managed `hasContractErrors` state.
 *
 * `errorStateTrigger` should be something that changes whenever engine errors change (e.g. the
 * `formErrors` object from `useFormValidationWithState`).
 */
export function useContractFlowAdapter(engine: ValidationEngine, errorStateTrigger?: unknown): UseContractFlowAdapterResult {
    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
    const featureToggles = useSelector(getTogglesWithoutSafetyChecks);

    // Store the latest contracting flag so compute logic can be stable.
    const isContractingContextRef = useRef(isContractingContext);
    useEffect(() => {
        isContractingContextRef.current = isContractingContext;
    }, [isContractingContext]);

    if (engine.ctx.featureToggles !== featureToggles) {
        engine.ctx.featureToggles = featureToggles as Record<string, boolean>;
    }

    useLayoutEffect(() => {
        engine.setFlow(isContractingContext ? 'default+contract' : 'default');
    }, [engine, isContractingContext]);

    // Attach helper method onto the validator instance to preserve object identity.
    // This method reads from refs so it does not need to be recreated per render.
    const contractEngine = engine as unknown as ContractFlowValidationEngine;
    if (!contractEngine.getHasContractErrors) {
        contractEngine.getHasContractErrors = () => {
            if (!isContractingContextRef.current) return false;

            const invalidPaths = Object.entries(contractEngine.getAllErrors())
                .filter(([, result]) => Boolean(result) && result.valid === false)
                .map(([path]) => path);

            if (!invalidPaths.length) return false;

            return contractEngine.getHasFormErrors({ flow: 'contract', includePaths: invalidPaths });
        };
    }

    const computeHasContractErrors = useCallback(() => {
        return contractEngine.getHasContractErrors();
    }, [contractEngine]);

    const [hasContractErrors, setHasContractErrors] = useState<boolean>(() => computeHasContractErrors());

    useEffect(() => {
        setHasContractErrors(computeHasContractErrors());
    }, [computeHasContractErrors, isContractingContext, errorStateTrigger]);

    return { engine: contractEngine, hasContractErrors };
}

/**
 * @deprecated Use the generic useFormValidation hook instead.
 */
export function useFormValidationWithContractFlow(options?: UseValidationEngineOptions): ContractFlowValidationEngine {
    const validator = useFormValidation(options);
    const { engine } = useContractFlowAdapter(validator);
    return engine;
}
